import React, { useEffect, useState } from 'react';

const GROUP_REG_API = process.env.REACT_APP_GROUP_REGISTRATION_API_URL || 'http://localhost:8086';

const STEP_TITLES = ['Personal', 'Correspondence', 'Summary'];

const initialForm = {
  email: '',
  title: '',
  firstName: '',
  lastName: '',
  phone: '',
  gender: '',
  country: '',
  state: '',
  city: '',
  address: '',
  zipCode: ''
};

const CorporateGroupRegistration = ({ onBackToLogin, groupLoginSession }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialForm);
  const [groupInfo, setGroupInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [createdRegistration, setCreatedRegistration] = useState(null);
  const [registeredCount, setRegisteredCount] = useState(0);
  const [addedEmployees, setAddedEmployees] = useState([]);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [activeSidebarView, setActiveSidebarView] = useState('add');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const normalizedEmployeeSearch = employeeSearch.trim().toLowerCase();
  const [isNarrowLayout, setIsNarrowLayout] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false
  );

  useEffect(() => {
    const loadGroupInfo = async () => {
      if (!groupLoginSession?.conferenceGroupId) {
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${GROUP_REG_API}/group-registration/groups/${groupLoginSession.conferenceGroupId}`);
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to load conference group details');
        }

        const data = await response.json();
        setGroupInfo(data);
      } catch (error) {
        console.error('Failed to load group details:', error);
        setAlert({ type: 'error', message: error.message || 'Unable to load conference group details' });
      } finally {
        setLoading(false);
      }
    };

    loadGroupInfo();
  }, [groupLoginSession]);

  useEffect(() => {
    const loadRegistrations = async () => {
      if (!groupLoginSession?.conferenceGroupId) {
        return;
      }

      try {
        const queryParams = new URLSearchParams({
          conferenceGroupId: String(groupLoginSession.conferenceGroupId),
          page: String(Math.max(currentPage - 1, 0)),
          size: String(rowsPerPage)
        });
        if (normalizedEmployeeSearch) {
          queryParams.set('search', normalizedEmployeeSearch);
        }

        const response = await fetch(
          `${GROUP_REG_API}/group-registration/registrations/paged?${queryParams.toString()}`
        );
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to load existing group registrations');
        }
        const data = await response.json();

        const registrations = Array.isArray(data?.content) ? data.content : [];
        const serverTotalElements = Number.isFinite(Number(data?.totalElements))
          ? Number(data.totalElements)
          : registrations.length;
        const serverTotalPages = Number.isFinite(Number(data?.totalPages)) && Number(data.totalPages) > 0
          ? Number(data.totalPages)
          : 1;

        setRegisteredCount(serverTotalElements);
        setTotalPages(serverTotalPages);
        setAddedEmployees(
          registrations
            .map((registration) => buildEmployeeRow(registration))
        );
      } catch (error) {
        console.error('Failed to load group registration count:', error);
      }
    };

    loadRegistrations();
  }, [groupLoginSession, currentPage, rowsPerPage, normalizedEmployeeSearch]);

  useEffect(() => {
    const onResize = () => {
      setIsNarrowLayout(window.innerWidth < 960);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const normalizedLimit = Number.isFinite(Number(groupInfo?.totalRegistrations))
    ? Number(groupInfo?.totalRegistrations)
    : 0;
  const hasRegistrationLimit = normalizedLimit > 0;
  const remainingSlots = hasRegistrationLimit ? Math.max(normalizedLimit - registeredCount, 0) : null;
  const canAddMoreEmployees = !hasRegistrationLimit || remainingSlots > 0;
  const paginatedEmployees = addedEmployees;
  const safeCurrentPage = Math.min(currentPage, Math.max(totalPages, 1));
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + paginatedEmployees.length, registeredCount);

  useEffect(() => {
    setCurrentPage(1);
  }, [normalizedEmployeeSearch]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    if (step === 1) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (
        !formData.email.trim() ||
        !emailPattern.test(formData.email.trim()) ||
        !formData.firstName.trim() ||
        !formData.lastName.trim() ||
        !formData.phone.trim() ||
        !formData.gender.trim()
      ) {
        setAlert({ type: 'error', message: 'Please complete all required personal details' });
        return false;
      }
    }

    if (step === 2) {
      if (!formData.country.trim() || !formData.state.trim() || !formData.city.trim() || !formData.address.trim() || !formData.zipCode.trim()) {
        setAlert({ type: 'error', message: 'Please complete all required correspondence details' });
        return false;
      }
    }

    setAlert(null);
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) {
      return;
    }
    setStep((prev) => Math.min(prev + 1, 3));
  };

  const handlePrevious = () => {
    setAlert(null);
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!groupLoginSession?.conferenceGroupId || !groupLoginSession?.loginEmail) {
      setAlert({ type: 'error', message: 'Group login session is missing. Please login again.' });
      return;
    }

    if (!groupInfo?.conference) {
      setAlert({ type: 'error', message: 'Conference group details not loaded yet.' });
      return;
    }

    const isEditMode = Boolean(editingEmployee);

    if (!isEditMode && !canAddMoreEmployees) {
      setAlert({ type: 'error', message: 'Cannot add more employees. Group registration limit reached.' });
      return;
    }

    setSubmitting(true);
    setAlert(null);

    try {
      const userPayload = {
        email: formData.email.trim().toLowerCase(),
        title: formData.title.trim() || null,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        gender: formData.gender.trim(),
        country: formData.country.trim(),
        state: formData.state.trim(),
        city: formData.city.trim(),
        address: formData.address.trim(),
        zipCode: formData.zipCode.trim()
      };

      const userRequestUrl = editingEmployee?.userId
        ? `${GROUP_REG_API}/group-registration/users/${editingEmployee.userId}`
        : `${GROUP_REG_API}/group-registration/users`;

      const normalizedUserPayload = {
        ...userPayload,
        email: userPayload.email.trim().toLowerCase()
      };

      const userSaveResponse = await fetch(userRequestUrl, {
        method: editingEmployee?.userId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalizedUserPayload)
      });

      if (!userSaveResponse.ok) {
        const text = await userSaveResponse.text();
        throw new Error(text || 'Failed to save user profile');
      }

      const user = await userSaveResponse.json();

      if (isEditMode) {
        const updatedEmployee = buildEmployeeRow(
          {
            id: editingEmployee.registrationId,
            user
          },
          formData
        );

        setAddedEmployees((prev) =>
          prev.map((employee) =>
            employee.registrationId === editingEmployee.registrationId ? updatedEmployee : employee
          )
        );
        setCreatedRegistration(null);
        setAlert({ type: 'success', message: 'Employee details updated successfully.' });
        setEditingEmployee(updatedEmployee);
        return;
      }

      const registrationPayload = {
        conference: groupInfo.conference,
        userId: user.id,
        conferenceGroupId: groupLoginSession.conferenceGroupId
      };

      const registrationResponse = await fetch(`${GROUP_REG_API}/group-registration/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationPayload)
      });

      if (!registrationResponse.ok) {
        const text = await registrationResponse.text();
        throw new Error(text || 'Failed to save group registration');
      }

      const registration = await registrationResponse.json();
      setCreatedRegistration(registration);
      setAddedEmployees((prev) => [buildEmployeeRow(registration, formData), ...prev]);
      const nextCount = registeredCount + 1;
      setRegisteredCount(nextCount);

      if (hasRegistrationLimit && nextCount >= normalizedLimit) {
        setAlert({ type: 'success', message: 'Group member registration saved. Registration limit has been reached.' });
      } else {
        setAlert({ type: 'success', message: 'Group member registration saved successfully. You can add next employee.' });
      }
    } catch (error) {
      console.error('Group member registration submit failed:', error);
      setAlert({ type: 'error', message: error.message || 'Failed to complete group member registration' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNextEmployee = () => {
    setFormData(initialForm);
    setCreatedRegistration(null);
    setEditingEmployee(null);
    setActiveSidebarView('add');
    setAlert(null);
    setStep(1);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setActiveSidebarView('add');
    setCreatedRegistration(null);
    setAlert(null);
    setFormData(mapEmployeeToFormData(employee));
    setStep(1);
  };

  const handleCancelEdit = () => {
    setEditingEmployee(null);
    setActiveSidebarView('add');
    setCreatedRegistration(null);
    setAlert(null);
    setFormData(initialForm);
    setStep(1);
  };

  const handleRemoveEmployee = async (employee) => {
    if (!employee?.registrationId) {
      setAlert({ type: 'error', message: 'Cannot remove this employee because registration ID is missing.' });
      return;
    }

    const confirmed = window.confirm(`Remove employee ${employee.email || ''}?`);
    if (!confirmed) {
      return;
    }

    setSubmitting(true);
    setAlert(null);
    try {
      const response = await fetch(`${GROUP_REG_API}/group-registration/registrations/${employee.registrationId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to remove employee');
      }

      setAddedEmployees((prev) => prev.filter((item) => item.registrationId !== employee.registrationId));
      setRegisteredCount((prev) => Math.max(prev - 1, 0));

      if (editingEmployee?.registrationId === employee.registrationId) {
        handleCancelEdit();
      }

      setCreatedRegistration(null);
      setAlert({ type: 'success', message: 'Employee removed successfully.' });
    } catch (error) {
      console.error('Failed to remove employee:', error);
      setAlert({ type: 'error', message: error.message || 'Failed to remove employee' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f5f9', padding: '24px 16px' }}>
      <div style={{ maxWidth: '920px', margin: '0 auto', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {groupInfo?.bannerUrl && (
          <div style={{ marginBottom: '24px', borderRadius: '8px', overflow: 'hidden' }}>
            <img src={groupInfo.bannerUrl} alt="Group Banner" style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0 }}>Group Registration</h2>
            <p style={{ marginTop: '8px', color: '#5f6b7a' }}>
              Complete Personal, Correspondence and Summary steps.
            </p>
          </div>
          <button
            type="button"
            onClick={onBackToLogin}
            style={{ padding: '10px 14px', border: '1px solid #d0d7de', borderRadius: '8px', background: '#fff', cursor: 'pointer' }}
          >
            Logout
          </button>
        </div>

        {groupLoginSession?.loginEmail && (
          <div style={{ marginTop: '14px', padding: '12px', background: '#f7f9fc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div><strong>Login Email:</strong> {groupLoginSession.loginEmail}</div>
            <div><strong>Conference Group ID:</strong> {groupLoginSession.conferenceGroupId}</div>
            <div><strong>Conference:</strong> {groupInfo?.conference || 'Loading...'}</div>
            <div>
              <strong>Registrations:</strong>{' '}
              {hasRegistrationLimit ? `${registeredCount} / ${normalizedLimit}` : `${registeredCount} (No cap configured)`}
            </div>
            {hasRegistrationLimit && remainingSlots === 0 && (
              <div style={{ color: '#8a1c2c', marginTop: '4px', fontWeight: 600 }}>
                Registration limit reached for this group.
              </div>
            )}
          </div>
        )}

        {editingEmployee && (
          <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '8px', background: '#fff8e6', border: '1px solid #f2d28b', color: '#7a5608' }}>
            Editing employee: <strong>{editingEmployee.email || '-'}</strong>
          </div>
        )}

        {alert && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px 14px',
              borderRadius: '8px',
              background: alert.type === 'error' ? '#fdecee' : '#e9f8ef',
              color: alert.type === 'error' ? '#8a1c2c' : '#17653c',
              border: `1px solid ${alert.type === 'error' ? '#f6c3cb' : '#bde7cd'}`
            }}
          >
            {alert.message}
          </div>
        )}

        {loading ? (
          <div style={{ marginTop: '20px', color: '#5f6b7a' }}>Loading group details...</div>
        ) : (
          <div style={isNarrowLayout ? workspaceLayoutStackedStyle : workspaceLayoutStyle}>
            <aside style={sidebarStyle}>
              <div style={sidebarTitleStyle}>Employee Actions</div>
              <button
                type="button"
                onClick={() => setActiveSidebarView('add')}
                style={activeSidebarView === 'add' ? activeSidebarButtonStyle : sidebarButtonStyle}
              >
                Add Employee
              </button>
              <button
                type="button"
                onClick={() => setActiveSidebarView('list')}
                style={activeSidebarView === 'list' ? activeSidebarButtonStyle : sidebarButtonStyle}
              >
                List Employees ({addedEmployees.length})
              </button>
              <div style={sidebarHintStyle}>
                {hasRegistrationLimit
                  ? `${remainingSlots} slot(s) remaining`
                  : 'No registration cap configured'}
              </div>
            </aside>

            <section style={contentPaneStyle}>
              {activeSidebarView === 'list' ? (
                <div style={listPanelStyle}>
                  <div style={listPanelHeaderRowStyle}>
                    <div style={listPanelHeaderStyle}>Added Employees</div>
                    <input
                      type="text"
                      value={employeeSearch}
                      onChange={(event) => setEmployeeSearch(event.target.value)}
                      placeholder="Search by email, name, phone, ID"
                      style={searchInputStyle}
                    />
                  </div>
                  {registeredCount === 0 ? (
                    <div style={{ padding: '12px 14px', color: '#5f6b7a' }}>
                      {normalizedEmployeeSearch ? 'No employees match your search.' : 'No employees added yet.'}
                    </div>
                  ) : (
                    <>
                      <div style={tableWrapperStyle}>
                        <table style={employeeTableStyle}>
                          <thead>
                            <tr>
                              <th style={employeeThStyle}>#</th>
                              <th style={employeeThStyle}>Registration ID</th>
                              <th style={employeeThStyle}>Email</th>
                              <th style={employeeThStyle}>Name</th>
                              <th style={employeeThStyle}>Phone</th>
                              <th style={employeeThStyle}>Gender</th>
                              <th style={employeeThStyle}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedEmployees.map((employee, index) => (
                              <tr key={`employee-${employee.registrationId || employee.email}`} style={index % 2 === 0 ? employeeTrEvenStyle : employeeTrOddStyle}>
                                <td style={employeeTdStyle}>{startIndex + index + 1}</td>
                                <td style={employeeTdStyle}>{employee.registrationId || '-'}</td>
                                <td style={employeeTdStyle}>{employee.email || '-'}</td>
                                <td style={employeeTdStyle}>{employee.name || '-'}</td>
                                <td style={employeeTdStyle}>{employee.phone || '-'}</td>
                                <td style={employeeTdStyle}>{employee.gender || '-'}</td>
                                <td style={employeeTdStyle}>
                                  <button type="button" onClick={() => handleEditEmployee(employee)} style={miniActionButtonStyle}>
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveEmployee(employee)}
                                    style={miniDangerButtonStyle}
                                    disabled={submitting}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={paginationContainerStyle}>
                        <div style={paginationInfoStyle}>
                          Showing {registeredCount === 0 ? 0 : startIndex + 1}-{registeredCount === 0 ? 0 : endIndex} of {registeredCount}
                        </div>
                        <div style={paginationButtonGroupStyle}>
                          <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={safeCurrentPage === 1}
                            style={safeCurrentPage === 1 ? paginationButtonDisabledStyle : paginationButtonStyle}
                          >
                            Previous
                          </button>
                          <span style={paginationPageTextStyle}>Page {safeCurrentPage} of {totalPages}</span>
                          <button
                            type="button"
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                            disabled={safeCurrentPage === totalPages}
                            style={safeCurrentPage === totalPages ? paginationButtonDisabledStyle : paginationButtonStyle}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {!editingEmployee && hasRegistrationLimit && remainingSlots === 0 && (
                    <div style={limitAlertStyle}>
                      Employee limit reached. Remove an employee from List Employees to add a new one.
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
                    {STEP_TITLES.map((title, index) => {
                      const stepNumber = index + 1;
                      const active = stepNumber === step;
                      const done = stepNumber < step;
                      return (
                        <div
                          key={title}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '999px',
                            background: active ? '#0d6efd' : done ? '#198754' : '#e9ecef',
                            color: active || done ? '#fff' : '#495057',
                            fontWeight: 600,
                            fontSize: '13px'
                          }}
                        >
                          {stepNumber}. {title}
                        </div>
                      );
                    })}
                  </div>

                  {step === 1 && (
                    <div style={gridStyle}>
                      <label style={labelStyle}>Employee Email *
                        <input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          style={inputStyle}
                          required
                        />
                      </label>
                      <label style={labelStyle}>Title
                        <input name="title" value={formData.title} onChange={handleInputChange} style={inputStyle} placeholder="Mr / Ms / Dr" />
                      </label>
                      <label style={labelStyle}>First Name *
                        <input name="firstName" value={formData.firstName} onChange={handleInputChange} style={inputStyle} required />
                      </label>
                      <label style={labelStyle}>Last Name *
                        <input name="lastName" value={formData.lastName} onChange={handleInputChange} style={inputStyle} required />
                      </label>
                      <label style={labelStyle}>Phone *
                        <input name="phone" value={formData.phone} onChange={handleInputChange} style={inputStyle} required />
                      </label>
                      <label style={labelStyle}>Gender *
                        <select name="gender" value={formData.gender} onChange={handleInputChange} style={inputStyle} required>
                          <option value="">Select Gender</option>
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </label>
                    </div>
                  )}

                  {step === 2 && (
                    <div style={gridStyle}>
                      <label style={labelStyle}>Country *
                        <input name="country" value={formData.country} onChange={handleInputChange} style={inputStyle} required />
                      </label>
                      <label style={labelStyle}>State *
                        <input name="state" value={formData.state} onChange={handleInputChange} style={inputStyle} required />
                      </label>
                      <label style={labelStyle}>City *
                        <input name="city" value={formData.city} onChange={handleInputChange} style={inputStyle} required />
                      </label>
                      <label style={labelStyle}>Address *
                        <input name="address" value={formData.address} onChange={handleInputChange} style={inputStyle} required />
                      </label>
                      <label style={labelStyle}>Zip Code *
                        <input name="zipCode" value={formData.zipCode} onChange={handleInputChange} style={inputStyle} required />
                      </label>
                    </div>
                  )}

                  {step === 3 && (
                    <div style={{ marginTop: '16px', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', background: '#f8fafc' }}>
                      <h3 style={{ marginTop: 0 }}>Summary</h3>
                      <div><strong>Conference:</strong> {groupInfo?.conference || '-'}</div>
                      <div><strong>Corporate Group:</strong> {groupInfo?.corporateName || '-'}</div>
                      <div><strong>Employee Email:</strong> {formData.email || '-'}</div>
                      <div><strong>Login Email:</strong> {groupLoginSession?.loginEmail || '-'}</div>
                      <div><strong>Name:</strong> {[formData.title, formData.firstName, formData.lastName].filter(Boolean).join(' ')}</div>
                      <div><strong>Phone:</strong> {formData.phone || '-'}</div>
                      <div><strong>Gender:</strong> {formData.gender || '-'}</div>
                      <div><strong>Address:</strong> {[formData.address, formData.city, formData.state, formData.country, formData.zipCode].filter(Boolean).join(', ')}</div>

                      {createdRegistration && (
                        <div style={{ marginTop: '14px', padding: '10px', borderRadius: '8px', background: '#e9f8ef', border: '1px solid #bde7cd' }}>
                          <div><strong>Saved Registration ID:</strong> {createdRegistration.id}</div>
                          <div><strong>Saved Conference:</strong> {createdRegistration.conference}</div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={step === 1 ? onBackToLogin : handlePrevious}
                      style={secondaryButtonStyle}
                    >
                      {step === 1 ? 'Logout' : 'Previous'}
                    </button>

                    {step < 3 ? (
                      <button type="button" onClick={handleNext} style={primaryButtonStyle}>Next</button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || (!editingEmployee && !canAddMoreEmployees)}
                        style={{
                          ...primaryButtonStyle,
                          opacity: submitting || (!editingEmployee && !canAddMoreEmployees) ? 0.65 : 1,
                          cursor: submitting || (!editingEmployee && !canAddMoreEmployees) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {submitting ? 'Saving...' : editingEmployee ? 'Update Employee' : 'Submit'}
                      </button>
                    )}
                  </div>

                  {editingEmployee && (
                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                      <button type="button" onClick={handleCancelEdit} style={secondaryButtonStyle}>
                        Cancel Edit
                      </button>
                    </div>
                  )}

                  {step === 3 && createdRegistration && !editingEmployee && canAddMoreEmployees && (
                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                      <button type="button" onClick={handleAddNextEmployee} style={secondaryButtonStyle}>
                        Add Next Employee
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const workspaceLayoutStyle = {
  display: 'grid',
  gridTemplateColumns: '30% 70%',
  gap: '16px',
  marginTop: '16px'
};

const workspaceLayoutStackedStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '16px',
  marginTop: '16px'
};

const sidebarStyle = {
  border: '1px solid #dbe3ef',
  borderRadius: '10px',
  background: '#f8fbff',
  padding: '12px',
  alignSelf: 'start'
};

const sidebarTitleStyle = {
  fontWeight: 700,
  color: '#1f2d3d',
  marginBottom: '10px'
};

const sidebarButtonStyle = {
  width: '100%',
  textAlign: 'left',
  border: '1px solid #d0d7de',
  background: '#fff',
  color: '#1f2937',
  borderRadius: '8px',
  padding: '10px 12px',
  marginBottom: '8px',
  fontWeight: 600,
  cursor: 'pointer'
};

const activeSidebarButtonStyle = {
  ...sidebarButtonStyle,
  border: '1px solid #0d6efd',
  background: '#eaf2ff',
  color: '#0d47a1'
};

const sidebarHintStyle = {
  marginTop: '8px',
  color: '#475569',
  fontSize: '12px',
  lineHeight: 1.4
};

const contentPaneStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  background: '#fcfdff',
  padding: '14px'
};

const listPanelStyle = {
  minHeight: '220px'
};

const listPanelHeaderStyle = {
  fontWeight: 700,
  color: '#27364a'
};

const listPanelHeaderRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  marginBottom: '10px',
  flexWrap: 'wrap'
};

const searchInputStyle = {
  minWidth: '260px',
  flex: '1 1 260px',
  maxWidth: '420px',
  padding: '9px 11px',
  border: '1px solid #cbd5e1',
  borderRadius: '8px',
  fontSize: '13px',
  color: '#334155',
  background: '#fff'
};

const tableWrapperStyle = {
  maxHeight: '520px',
  overflowY: 'auto',
  overflowX: 'auto',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  background: '#fff'
};

const employeeTableStyle = {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  minWidth: '860px',
  fontSize: '13px',
  color: '#334155'
};

const paginationContainerStyle = {
  marginTop: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  flexWrap: 'wrap'
};

const paginationInfoStyle = {
  fontSize: '12px',
  color: '#475569'
};

const paginationButtonGroupStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
};

const paginationPageTextStyle = {
  fontSize: '12px',
  color: '#334155',
  fontWeight: 600,
  minWidth: '90px',
  textAlign: 'center'
};

const paginationButtonStyle = {
  padding: '6px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  background: '#fff',
  color: '#1e293b',
  fontWeight: 600,
  cursor: 'pointer'
};

const paginationButtonDisabledStyle = {
  ...paginationButtonStyle,
  opacity: 0.55,
  cursor: 'not-allowed'
};

const employeeThStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  textAlign: 'left',
  padding: '10px 12px',
  background: '#f1f5f9',
  color: '#1e293b',
  borderBottom: '1px solid #cbd5e1',
  fontWeight: 700,
  whiteSpace: 'nowrap'
};

const employeeTdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #e2e8f0',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
};

const employeeTrEvenStyle = {
  background: '#ffffff'
};

const employeeTrOddStyle = {
  background: '#f8fafc'
};

const limitAlertStyle = {
  marginTop: '4px',
  padding: '10px 12px',
  borderRadius: '8px',
  background: '#fff1f2',
  border: '1px solid #fecdd3',
  color: '#9f1239',
  fontWeight: 600,
  fontSize: '14px'
};

const gridStyle = {
  marginTop: '16px',
  display: 'grid',
  gap: '14px',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  color: '#3d4957',
  fontSize: '14px'
};

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #d0d7de',
  borderRadius: '8px',
  fontSize: '14px'
};

const primaryButtonStyle = {
  padding: '10px 16px',
  border: 'none',
  borderRadius: '8px',
  background: '#0d6efd',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer'
};

const secondaryButtonStyle = {
  padding: '10px 16px',
  border: '1px solid #d0d7de',
  borderRadius: '8px',
  background: '#fff',
  color: '#212529',
  fontWeight: 600,
  cursor: 'pointer'
};

const miniActionButtonStyle = {
  padding: '6px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  background: '#fff',
  color: '#1e293b',
  fontWeight: 600,
  marginRight: '8px',
  cursor: 'pointer'
};

const miniDangerButtonStyle = {
  padding: '6px 10px',
  border: '1px solid #fecaca',
  borderRadius: '6px',
  background: '#fff5f5',
  color: '#b91c1c',
  fontWeight: 600,
  cursor: 'pointer'
};

function buildEmployeeRow(registration, fallbackFormData = null) {
  const user = registration?.user || {};
  const fullName = [user.title, user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  const fallbackName = fallbackFormData
    ? [fallbackFormData.title, fallbackFormData.firstName, fallbackFormData.lastName].filter(Boolean).join(' ').trim()
    : '';

  return {
    userId: user.id || null,
    registrationId: registration?.id || null,
    email: (user.email || fallbackFormData?.email || '').trim(),
    name: fullName || fallbackName || '-',
    title: (user.title || fallbackFormData?.title || '').trim(),
    firstName: (user.firstName || fallbackFormData?.firstName || '').trim(),
    lastName: (user.lastName || fallbackFormData?.lastName || '').trim(),
    phone: (user.phone || fallbackFormData?.phone || '').trim(),
    gender: (user.gender || fallbackFormData?.gender || '').trim(),
    country: (user.country || fallbackFormData?.country || '').trim(),
    state: (user.state || fallbackFormData?.state || '').trim(),
    city: (user.city || fallbackFormData?.city || '').trim(),
    address: (user.address || fallbackFormData?.address || '').trim(),
    zipCode: (user.zipCode || fallbackFormData?.zipCode || '').trim()
  };
}

function mapEmployeeToFormData(employee) {
  return {
    email: employee?.email || '',
    title: employee?.title || '',
    firstName: employee?.firstName || '',
    lastName: employee?.lastName || '',
    phone: employee?.phone || '',
    gender: employee?.gender || '',
    country: employee?.country || '',
    state: employee?.state || '',
    city: employee?.city || '',
    address: employee?.address || '',
    zipCode: employee?.zipCode || ''
  };
}

export default CorporateGroupRegistration;
